import { useState, useEffect } from 'react';
import { get, put, del } from 'aws-amplify/api';
import { FullUserProfile, CompanyProfile } from '../../types';

// SVG Icon Components for Edit and Delete actions
const EditIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#5934C4'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'></path>
    <path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'></path>
  </svg>
);

const DeleteIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#E53E3E'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polyline points='3 6 5 6 21 6'></polyline>
    <path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2'></path>
    <line x1='10' y1='11' x2='10' y2='17'></line>
    <line x1='14' y1='11' x2='14' y2='17'></line>
  </svg>
);

export function UserListManager() {
  const [users, setUsers] = useState<FullUserProfile[]>([]);
  const [companies, setCompanies] = useState<CompanyProfile[]>([]);
  const [editingUser, setEditingUser] = useState<FullUserProfile | null>(null);
  const [message, setMessage] = useState('');

  const fetchUsersAndCompanies = async () => {
    setMessage('Loading users...');
    try {
      const [usersRes, companiesRes] = await Promise.all([
        get({ apiName: 'aslanwebsftpapi', path: '/admin/users' }).response,
        get({ apiName: 'aslanwebsftpapi', path: '/companies' }).response,
      ]);

      const userList =
        (await usersRes.body.json()) as unknown as FullUserProfile[];
      const companyList =
        (await companiesRes.body.json()) as unknown as CompanyProfile[];

      setUsers(userList);
      setCompanies(companyList);
      setMessage('');
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage('Could not load users.');
    }
  };

  useEffect(() => {
    fetchUsersAndCompanies();
  }, []);

  const handleUpdateUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingUser) return;

    setMessage('Updating user...');
    const updatedData = {
      firstName: (
        e.currentTarget.elements.namedItem('firstName') as HTMLInputElement
      ).value,
      lastName: (
        e.currentTarget.elements.namedItem('lastName') as HTMLInputElement
      ).value,
      companyId: (
        e.currentTarget.elements.namedItem('companyId') as HTMLSelectElement
      ).value,
      canUpload: (
        e.currentTarget.elements.namedItem('canUpload') as HTMLInputElement
      ).checked,
      enabled:
        (
          e.currentTarget.elements.namedItem(
            'cognitoStatus'
          ) as HTMLSelectElement
        ).value === 'ENABLED',
    };

    try {
      await put({
        apiName: 'aslanwebsftpapi',
        path: `/admin/users/${editingUser.id}`,
        options: { body: updatedData },
      }).response;
      setMessage('User updated successfully.');
      setEditingUser(null);
      await fetchUsersAndCompanies();
    } catch (error) {
      console.error('Error updating user:', error);
      setMessage('Failed to update user.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (
      window.confirm(
        'Are you sure you want to soft-delete this user? They will be disabled and marked as DELETED.'
      )
    ) {
      try {
        await del({
          apiName: 'aslanwebsftpapi',
          path: `/admin/users/${userId}`,
        }).response;
        setMessage('User soft-deleted successfully.');
        await fetchUsersAndCompanies();
      } catch (error) {
        console.error('Error deleting user:', error);
        setMessage('Failed to delete user.');
      }
    }
  };

  const UserEditForm = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <form
        onSubmit={handleUpdateUser}
        style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          width: '400px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <h4>Edit User: {editingUser?.email}</h4>
        <div>
          <label>
            First Name:{' '}
            <input
              type='text'
              name='firstName'
              defaultValue={editingUser?.firstName}
              required
              style={{ width: '100%' }}
            />
          </label>
        </div>
        <div>
          <label>
            Last Name:{' '}
            <input
              type='text'
              name='lastName'
              defaultValue={editingUser?.lastName}
              required
              style={{ width: '100%' }}
            />
          </label>
        </div>
        <div>
          <label>
            Company:
            <select
              name='companyId'
              defaultValue={editingUser?.companyId}
              required
              style={{ width: '100%' }}
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div>
          <label>
            <input
              type='checkbox'
              name='canUpload'
              defaultChecked={editingUser?.canUpload}
            />{' '}
            Grant Upload Permission
          </label>
        </div>
        <div>
          <label>
            Account Status:
            <select
              name='cognitoStatus'
              defaultValue={editingUser?.enabled ? 'ENABLED' : 'DISABLED'}
              style={{ width: '100%' }}
            >
              <option value='ENABLED'>Enabled</option>
              <option value='DISABLED'>Disabled</option>
            </select>
          </label>
        </div>
        <div style={{ marginTop: '10px' }}>
          <button type='submit'>Save Changes</button>
          <button
            type='button'
            onClick={() => setEditingUser(null)}
            style={{ marginLeft: '10px' }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div>
      <h3>Manage Users</h3>
      {message && (
        <p>
          <em>{message}</em>
        </p>
      )}
      {editingUser && <UserEditForm />}
      <table
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}
      >
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '2px solid #EEEBF9' }}>
            <th style={{ padding: '12px 8px' }}>Email</th>
            <th style={{ padding: '12px 8px' }}>Name</th>
            <th style={{ padding: '12px 8px' }}>Company</th>
            <th style={{ padding: '12px 8px' }}>Can Upload</th>
            <th style={{ padding: '12px 8px' }}>DB Status</th>
            <th style={{ padding: '12px 8px' }}>Cognito Status</th>
            <th style={{ padding: '12px 8px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} style={{ borderBottom: '1px solid #EEEBF9' }}>
              <td style={{ padding: '12px 8px' }}>{user.email}</td>
              <td style={{ padding: '12px 8px' }}>
                {user.firstName} {user.lastName}
              </td>
              <td style={{ padding: '12px 8px' }}>
                {user.company?.name || 'N/A'}
              </td>
              <td style={{ padding: '12px 8px' }}>
                {user.canUpload ? 'Yes' : 'No'}
              </td>
              <td style={{ padding: '12px 8px' }}>{user.dbStatus}</td>
              <td style={{ padding: '12px 8px' }}>
                {user.cognitoStatus} {user.enabled ? '' : '(Disabled)'}
              </td>
              <td style={{ padding: '12px 8px', display: 'flex', gap: '15px' }}>
                <span
                  onClick={() => setEditingUser(user)}
                  style={{ cursor: 'pointer' }}
                >
                  <EditIcon />
                </span>
                {user.dbStatus !== 'DELETED' && (
                  <span
                    onClick={() => handleDeleteUser(user.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <DeleteIcon />
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
